import React from 'react';

import { useSpring, animated, easings } from '@react-spring/web';
import config from 'config';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { TextField, Checkbox } from '@techstyle/react-components';
import { Checkbox as CheckboxLegacy } from '@techstyle/react-components-legacy';

import { desktop, mobile, oldMobileSafari } from '../../styles';
import { BasicButton } from '../../styles/button';

const cdnUrl = config.get('public.cdn.baseUrl');
const storeGroups = config.get('public.domain.storeGroups');

export const SelectAllButton = styled(BasicButton).attrs({
  tabIndex: 0,
})`
  ${({ theme, isSelected }) =>
    isSelected
      ? css`
          ${theme.button.variants.primaryLight.buttonStyle}
        `
      : css`
          ${theme.button.variants.secondaryLight.buttonStyle}
        `};
  ${desktop`
    width: 300px;
  `}
  ${mobile`
    width: calc(100% - 32px);
    margin: ${({ theme }) => theme.spacing.moderate}px ${({ theme }) =>
    theme.spacing.small}px 0 ${({ theme }) => theme.spacing.small}px;
  `}
`;

const inputBoxStyle = css`
  ${({ theme }) => theme.inputBox.defaultStyle};
  border-color: ${props =>
    props.disabled ? props.theme.colors.subdued : props.theme.colors.default};
`;

const inputStyle = css`
  ${({ theme }) => theme.input.defaultStyle};
`;

const labelStyle = css`
  ${({ theme }) => theme.labelInput.defaultStyle};
  color: ${props =>
    props.disabled ? props.theme.colors.subdued : props.theme.colors.default};
`;

const errorStyle = css`
  ${({ theme }) => theme.errorInput.defaultStyle};
`;

export const StyledTextInput = styled(TextField).attrs(props => {
  return {
    errorStyle: errorStyle,
    inputBoxStyle: inputBoxStyle,
    inputStyle: inputStyle,
    labelStyle: labelStyle,
  };
})`
  font-size: 16px;
  letter-spacing: 0.34px;
`;

const checkboxInputStyle = css`
  margin-right: ${({ theme }) => theme.spacing.tiny}px;
  padding-top: 2px;
  line-height: 13px;
  color: ${({ theme }) => theme.colors.default};
  text-align: center;
  border-width: 1px;
  border-style: solid;
  font-size: 14px;

  width: 24px;
  height: 24px;
  opacity: 0px;
  border-color: ${({ theme }) => theme.colors.white};
  background-color: transparent;
  border-radius: 0;
`;

const inputCheckedStyle = css`
  color: ${({ theme }) => theme.colors.white};
  background-color: transparent;
  border-color: ${({ theme }) => theme.colors.white};
`;

const inputFocusStyle = css`
  box-shadow: none;
`;

const checkmarkStyle = css`
  height: 18px;
  width: 18px;
`;

const checkmarkCheckingStyle = css`
  opacity: 0;
`;

const checkmarkUncheckingStyle = css`
  opacity: 1;
`;
const checkboxInputStyleWithErrors = css`
  ${checkboxInputStyle}
  border-color: ${({ theme }) => theme.colors.error};
`;

export const QuizCheckmarkIcon = (
  <svg
    width="18"
    height="14"
    viewBox="0 0 18 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.409 0.292892L17.8945 1.77836L6.42095 13.2519L0.292847 7.12382L1.77832 5.63835L6.42095 10.281L16.409 0.292892Z"
      fill="white"
    />
  </svg>
);

export const StyledCheckbox = styled(Checkbox).attrs(({ hasError }) => {
  return {
    inputStyle: hasError ? checkboxInputStyleWithErrors : checkboxInputStyle,
    inputCheckedStyle: inputCheckedStyle,
    inputFocusStyle,
    checkmarkStyle,
    checkmarkCheckingStyle,
    checkmarkUncheckingStyle,
  };
})``;

export const CheckboxWrapper = styled.label`
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  line-height: 20px;

  text-align: left;
  letter-spacing: 0.3px;
  position: relative;
  bottom: unset;
  margin: 0 20px ${({ theme }) => theme.spacing.small}px;

  span {
    display: block;
    margin-bottom: 5px;
    margin-left: ${({ theme }) => theme.spacing.tiny}px;
  }
`;

export const SelectWrapper = styled.div`
  width: 100%;
  ${desktop`
    width: 40%;
  `}
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.tiny}px;
  justify-content: center;
  flex-wrap: wrap;
  align-content: center;
  flex: 1;
`;

export const OneSizeCheckbox = styled(CheckboxLegacy)`
  display: inline-block;
  > ${CheckboxLegacy.InputWrapper} {
    border: none;
    position: relative;
    justify-content: center;
    > ${CheckboxLegacy.CustomCheckbox} {
      width: 62px;
      height: 62px;
      border-radius: 50%;
      border: 1px solid ${({ theme }) => theme.colors.lavender250};
      background-color: ${({ theme }) => theme.colors.white};
      margin: 0;

      > svg {
        display: none;
      }
    }

    > ${CheckboxLegacy.Label} {
      position: absolute;
      color: ${({ theme }) => theme.colors.black};
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      line-height: 17px;
      font-weight: normal;
      text-transform: uppercase;
      text-align: center;
      padding: 4px;

      ${desktop`
        font-size: 16px;
        line-height: 19px;
      `};
    }
    input:checked ~ ${CheckboxLegacy.Label} {
      color: ${({ theme }) => theme.colors.white};
    }
    input:active
      + ${CheckboxLegacy.CustomCheckbox},
      input:focus
      + ${CheckboxLegacy.CustomCheckbox} {
      border: 1px solid ${({ theme }) => theme.colors.active};
    }
    input:checked + ${CheckboxLegacy.CustomCheckbox} {
      background-color: ${({ theme }) => theme.colors.active};
    }
    input:checked:focus + ${CheckboxLegacy.CustomCheckbox} {
      border: 1px solid ${({ theme }) => theme.colors.active};
      border-radius: 100%;
      background-clip: content-box;
      padding: 1px;
    }
  }

  ${oldMobileSafari`
    margin: 4px;
  `}
`;

const StyledBasicButton = styled(BasicButton).attrs({
  tabIndex: 0,
})`
  width: 219px;
  background: ${({ theme, inverse }) =>
    inverse ? theme.colors.white : theme.colors.default};
  color: ${({ theme, inverse }) =>
    inverse ? theme.colors.default : theme.colors.white};
  text-transform: uppercase;
  border: 0px;
  box-shadow: 0px 4px 8px ${({ theme }) => theme.colors.lightShadow2};

  ${({ theme }) =>
    css`
      ${theme.button.variants.primaryLight.buttonStyle}
    `}}
  ${({ isValid }) =>
    isValid
      ? `opacity: 1;`
      : `
          opacity: 0.5;
          pointer-events: none;
      `}
  ${desktop`
    width: 300px;
  `}
`;

const AnimatedBtnWrapper = styled(animated.div)``;

export const NextButton = ({
  children,
  className,
  delay = 100,
  duration = 300,
  inverse,
  onClick,
  shouldAnimate,
  isValid = false,
}) => {
  return (
    <AnimatedBtnWrapper className={className}>
      <StyledBasicButton
        data-autotag="product-quiz-next-btn"
        onClick={onClick}
        inverse={inverse}
        isValid={isValid}
      >
        {children}
      </StyledBasicButton>
    </AnimatedBtnWrapper>
  );
};

NextButton.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  className: PropTypes.string,
  delay: PropTypes.number,
  duration: PropTypes.number,
  inverse: PropTypes.bool,
  onClick: PropTypes.func,
  shouldAnimate: PropTypes.bool,
};

const StyledNextButton = styled(NextButton)`
  align-self: center;
  margin-bottom: ${({ theme }) => theme.spacing.moderate}px;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.mobile.maxWidth}px) and (orientation: portrait) {
    width: 100%;

    & button {
      width: 100%;
    }
  }
  ${({ nextButtonStyle }) => nextButtonStyle}
`;

const TakeQuizButton = styled(StyledNextButton)`
  margin-bottom: 0;
  ${({ takeQuizStyle }) => takeQuizStyle}
`;

const SkipQuizButton = styled(StyledNextButton)`
  & button {
    background-color: transparent;
    border-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    box-shadow: none;
  }
  ${({ skipQuizStyle }) => skipQuizStyle}
`;

export const ScreenWrapperContainer = styled.div`
  align-items: center;
  ${({ theme, tld, showBackground }) =>
    showBackground &&
    `
      background-color: ${theme.colors.lavender100};
      background-image: url(${cdnUrl}/media/images/quiz/${
      storeGroups[tld] ? storeGroups[tld] : 34
    }/quiz_background.jpg);
      background-size: cover;
      background-position: center;
  `}

  cursor: default;
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const AnimatedScreenWrapper = styled(animated.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: default;
  height: 100%;
  width: 100%;
`;

export const AnimatedScreen = ({ className, children }) => {
  const styles = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: {
      duration: 500,
      easing: easings.easeInQuad,
    },
    delay: 100,
  });

  return (
    <AnimatedScreenWrapper className={className} style={styles}>
      {children}
    </AnimatedScreenWrapper>
  );
};

AnimatedScreen.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export const ScreenWrapper = ({
  className = '',
  children,
  tld,
  showBackground = true,
}) => (
  <ScreenWrapperContainer
    className={className}
    tld={tld}
    showBackground={showBackground}
  >
    <AnimatedScreen>{children}</AnimatedScreen>
  </ScreenWrapperContainer>
);

ScreenWrapper.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  tld: PropTypes.string,
  showBackground: PropTypes.bool,
};

ScreenWrapper.AnimatedScreenWrapper = AnimatedScreenWrapper;

export const QuizIntroCtas = ({
  ctaTexts,
  delay = 500,
  duration = 400,
  isButtonInversed,
  nextButtonStyle,
  onHandleNext = () => {},
  onHandleSkip = () => {},
  onHandleTake = () => {},
  shouldAnimate = true,
  shouldShowTakeAndSkipCta,
  skipQuizStyle,
  takeQuizStyle,
}) => {
  const { button, buttonChoice, skip } = ctaTexts;

  if (shouldShowTakeAndSkipCta) {
    return (
      <>
        <TakeQuizButton
          onClick={onHandleTake}
          takeQuizStyle={takeQuizStyle}
          shouldAnimate={shouldAnimate}
          inverse={isButtonInversed}
          duration={duration}
          delay={delay}
        >
          {buttonChoice}
        </TakeQuizButton>
        <SkipQuizButton
          onClick={onHandleSkip}
          skipQuizStyle={skipQuizStyle}
          shouldAnimate={shouldAnimate}
          duration={duration}
          delay={delay}
        >
          {skip}
        </SkipQuizButton>
      </>
    );
  }

  return (
    <StyledNextButton
      className
      nextButtonStyle={nextButtonStyle}
      onClick={onHandleNext}
      shouldAnimate={shouldAnimate}
      inverse={isButtonInversed}
      duration={duration}
      delay={delay}
    >
      {button}
    </StyledNextButton>
  );
};

QuizIntroCtas.propTypes = {
  ctaTexts: PropTypes.object,
  delay: PropTypes.number,
  duration: PropTypes.number,
  isButtonInversed: PropTypes.bool,
  nextButtonStyle: PropTypes.array,
  onHandleNext: PropTypes.func,
  onHandleSkip: PropTypes.func,
  onHandleTake: PropTypes.func,
  shouldAnimate: PropTypes.bool,
  shouldShowTakeAndSkipCta: PropTypes.bool,
  skipQuizStyle: PropTypes.array,
  takeQuizStyle: PropTypes.array,
};
